import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { isApiError } from './client';
import { toast } from '../ui/toast';

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { silent?: boolean };
    mutationMeta: { silent?: boolean };
  }
}

/** Shows a toast for any error the UI didn't opt out of. 401s are handled centrally. */
export function reportError(err: unknown) {
  if (isApiError(err)) {
    if (err.status === 401) return;
    toast.error(err.message);
    return;
  }
  toast.error(err instanceof Error && err.message ? err.message : 'Something went wrong.');
}

export function createQueryClient() {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (err, query) => {
        if (query.meta?.silent) return;
        // Background refetch failures for data we already show: only toast network/server issues once.
        reportError(err);
      },
    }),
    mutationCache: new MutationCache({
      onError: (err, _vars, _ctx, mutation) => {
        if (mutation.meta?.silent) return;
        reportError(err);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        retry: (count, err) => {
          if (isApiError(err) && err.status >= 400 && err.status < 500) return false;
          return count < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}
